import { Injectable } from '@angular/core';
import { AccountService } from '../../../shared/services/account.service';
import { FeatureService } from './feature.service';
import { WidgetState } from '../../widget/state/widget_state';
import { forkJoin, Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { IAppState } from '../../../app_state';
import { LoyaltyHttpService } from '../../../shared/services/loyalty_http.service';
import { map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';
import { WidgetMacaroonInstancesService } from '@reviews-app/reviews-shared';

export interface WidgetCustomization {
  response: {
    font_type?: string;
    color_stars: string;
    color_primary: string;
  };
}

export const widgetsTypes = {
  mainWidget: 'ReviewsMainWidget',
  starRatingsWidget: 'ReviewsStarRatingsWidget',
  questionsAndAnswers: 'QuestionsAndAnswers',
  promotedProducts: 'PromotedProducts',
  reviewsCarousel: 'ReviewsCarousel',
  vugcMediaGallery: 'VugcMediaGallery',
  reviewsTab: 'ReviewsTab'
};

@Injectable()
export class WidgetService {
  private isWidgetV3: boolean;

  constructor(private accountService: AccountService,
              private store: Store<IAppState>,
              private loyaltyHttpService: LoyaltyHttpService,
              private widgetMacaroonInstancesService: WidgetMacaroonInstancesService,
              private featureService: FeatureService) {

    this.store.select(state => state.app.install.integrate.hasWidgetV3).subscribe(
      (hasWidgetV3: boolean) => {
        this.isWidgetV3 = hasWidgetV3;
      });
  }

  getWidgetV3InstanceId(widgetType: string): Observable<number> {
    return this.featureService.isFeatureEnabled('WidgetV3').pipe(
      switchMap(hasWidgetV3 => {
        if (hasWidgetV3) {
          return this.widgetMacaroonInstancesService.getWidgetInstance(widgetType);
        } else {
          return of(null);
        }
      }));
  }

  saveWidgetData(state: WidgetState) {
    const updateAppData = this.accountService.updateAppData({auto_publish: state.autoPublish});
    const updateWidgetCss = this.isWidgetV3 ? this.setReviewsMainWidgetV3Customizations(state) : this.setReviewsMainWidgetV2Customizations(state);
    let toFork = [updateAppData, updateWidgetCss];
    return forkJoin(...toFork);
  }

  setReviewsMainWidgetV2Customizations(state: WidgetState): any {
    return this.featureService.setFeatureSettings('LookAndFeel',
      {
        settings: {
          color_primary: state.primaryColor,
          color_stars: state.starRatingColor,
          font_type: state.font
        }
      }
    );
  }

  setReviewsMainWidgetV3Customizations(state: WidgetState): any {
    const params = {
      widgets_v3_auth: true,
      widget_name: widgetsTypes.mainWidget,
      partial_update: true,
      customizations: {
        'view-stars-color': state.starRatingColor,
        'view-primary-color': state.primaryColor
      }
    };
    return this.loyaltyHttpService.put(`/api/v2/widgets/${state.widgetV3InstanceId}`, params);
  }

  fetchWidgetCustomizations(widgetInstanceId: number): Observable<WidgetCustomization> {
    if (widgetInstanceId) {
      return this.getWidgetV3Customizations(widgetInstanceId);
    } else {
      return this.getWidgetV2Customizations();
    }
  }

  getWidgetV2Customizations(): Observable<WidgetCustomization> {
    return this.featureService.getFeatureSettings('LookAndFeel');
  }

  getWidgetV3Customizations(widgetInstanceId: number): Observable<WidgetCustomization> {
    return this.loyaltyHttpService.get(`/api/v2/widgets/${widgetInstanceId}?widgets_v3_auth=true`).pipe(
      map((reviewsWidgetsResponse: any) => {
          return {
            response: {
              color_stars: reviewsWidgetsResponse['customizations']['view-stars-color'],
              color_primary: reviewsWidgetsResponse['customizations']['view-primary-color']
            }
          };
        }
      )
    );
  }

}

